import React, { useState, useEffect, useRef } from 'react';
import { navigate } from "@reach/router";
import StoreContext, { defaultStoreContext } from './store';

const isBrowser = typeof window !== 'undefined';

const logProviderDebug = (event, details = {}) => {
    if (!isBrowser) {
        return;
    }

    console.log(`[shopify-provider] ${event}`, details);
};

const removeZeroQuantityLinesFromCheckout = async (checkout, removeLineItems) => {
    const zeroQuantityLineIds = checkout?.lineItems
        ?.filter((lineItem) => lineItem.quantity < 1)
        .map((lineItem) => lineItem.id) || [];

    if (zeroQuantityLineIds.length === 0) {
        return checkout;
    }

    logProviderDebug('checkout:removingZeroQuantityLines', {
        cartId: checkout.id,
        lineIds: zeroQuantityLineIds,
    });

    return removeLineItems(checkout.id, zeroQuantityLineIds);
};

const shouldRebuildCart = (error, checkout) => {
    if (!error?.message?.includes('MERCHANDISE_OUT_OF_STOCK')) {
        return false;
    }

    return checkout?.lineItems?.some((lineItem) => lineItem.quantity < 1);
};

const Provider = ({ children }) => {
    const [store, updateStore] = useState(defaultStoreContext);
    const checkoutInitRef = useRef(null);

    const getlocalStorage = (value) => {
        try {
            return JSON.parse(localStorage.getItem(value));
        } catch (e) {
            return '';
        }
    };

    useEffect(() => {
        const initializeCheckout = async () => {
            const existingCheckoutID = isBrowser ? localStorage.getItem('shopify_checkout_id') : null;
            const validExistingCheckoutID = existingCheckoutID && existingCheckoutID !== 'null' ? existingCheckoutID : null;

            const setCheckoutInState = (checkout) => {
                if (isBrowser) {
                    localStorage.setItem('shopify_checkout_id', checkout.id);
                }
                updateStore((state) => ({ ...state, checkout }));
                return checkout;
            };

            const { createCart, fetchCart, removeLineItems, updateCartBuyerIdentity } = await import('../utils/storefront-client');

            if (validExistingCheckoutID) {
                try {
                    let checkout = await fetchCart(validExistingCheckoutID);

                    logProviderDebug('initializeCheckout:existingCart', {
                        cartId: validExistingCheckoutID,
                        countryCode: checkout?.buyerIdentity?.countryCode,
                    });

                    if (checkout?.buyerIdentity?.countryCode !== 'GB') {
                        logProviderDebug('initializeCheckout:updatingCountry', {
                            cartId: validExistingCheckoutID,
                            from: checkout?.buyerIdentity?.countryCode,
                            to: 'GB',
                        });
                        checkout = await updateCartBuyerIdentity(validExistingCheckoutID, 'GB');
                    }

                    checkout = await removeZeroQuantityLinesFromCheckout(checkout, removeLineItems);

                    if (checkout && !checkout.completedAt) {
                        return setCheckoutInState(checkout);
                    }
                } catch (e) {
                    localStorage.removeItem('shopify_checkout_id');
                }
            }

            const newCheckout = await createCart();
            logProviderDebug('initializeCheckout:newCart', {
                cartId: newCheckout?.id,
                countryCode: newCheckout?.buyerIdentity?.countryCode,
            });
            return setCheckoutInState(newCheckout);
        };

        checkoutInitRef.current = initializeCheckout();
    }, []);

    return (
        <StoreContext.Provider
            value={{
                store,
                customerAccessToken: getlocalStorage('customerAccessToken'),
                addVariantToCart: async (variantId, quantity) => {
                    updateStore((state) => ({ ...state, adding: true }));
                    try {
                        let checkout = store.checkout?.id ? store.checkout : await checkoutInitRef.current;
                        const lineItemsToAdd = [{ merchandiseId: variantId, quantity: parseInt(quantity, 10) }];

                        logProviderDebug('addVariantToCart:beforeCheck', {
                            cartId: checkout.id,
                            countryCode: checkout?.buyerIdentity?.countryCode,
                            variantId,
                        });

                        const { addLineItems, createCart, fetchCart, removeLineItems, updateCartBuyerIdentity } = await import('../utils/storefront-client');

                        checkout = await fetchCart(checkout.id);

                        if (checkout?.buyerIdentity?.countryCode !== 'GB') {
                            logProviderDebug('addVariantToCart:updatingCountry', {
                                cartId: checkout.id,
                                from: checkout?.buyerIdentity?.countryCode,
                                to: 'GB',
                            });
                            checkout = await updateCartBuyerIdentity(checkout.id, 'GB');
                            updateStore((state) => ({ ...state, checkout }));
                        }

                        checkout = await removeZeroQuantityLinesFromCheckout(checkout, removeLineItems);
                        updateStore((state) => ({ ...state, checkout }));

                        let newCheckout;

                        try {
                            newCheckout = await addLineItems(checkout.id, lineItemsToAdd);
                        } catch (error) {
                            const latestCheckout = await fetchCart(checkout.id);

                            if (!shouldRebuildCart(error, latestCheckout)) {
                                throw error;
                            }

                            logProviderDebug('addVariantToCart:rebuildingCart', {
                                cartId: checkout.id,
                                variantId,
                            });

                            const freshCheckout = await createCart();
                            updateStore((state) => ({ ...state, checkout: freshCheckout }));
                            newCheckout = await addLineItems(freshCheckout.id, lineItemsToAdd);
                        }

                        updateStore((state) => ({ ...state, checkout: newCheckout, adding: false }));
                        return newCheckout;
                    } catch (error) {
                        updateStore((state) => ({ ...state, adding: false }));
                        throw error;
                    }
                },
                addVariantToCartAndBuyNow: async (variantId, quantity) => {
                    updateStore((state) => ({ ...state, adding: true }));
                    try {
                        let checkout = store.checkout?.id ? store.checkout : await checkoutInitRef.current;
                        const lineItemsToAdd = [{ merchandiseId: variantId, quantity: parseInt(quantity, 10) }];

                        const { addLineItems, createCart, fetchCart, removeLineItems, updateCartBuyerIdentity } = await import('../utils/storefront-client');

                        checkout = await fetchCart(checkout.id);

                        if (checkout?.buyerIdentity?.countryCode !== 'GB') {
                            checkout = await updateCartBuyerIdentity(checkout.id, 'GB');
                            updateStore((state) => ({ ...state, checkout }));
                        }

                        checkout = await removeZeroQuantityLinesFromCheckout(checkout, removeLineItems);
                        updateStore((state) => ({ ...state, checkout }));

                        let newCheckout;

                        try {
                            newCheckout = await addLineItems(checkout.id, lineItemsToAdd);
                        } catch (error) {
                            const latestCheckout = await fetchCart(checkout.id);

                            if (!shouldRebuildCart(error, latestCheckout)) {
                                throw error;
                            }

                            logProviderDebug('addVariantToCartAndBuyNow:rebuildingCart', {
                                cartId: checkout.id,
                                variantId,
                            });

                            const freshCheckout = await createCart();
                            updateStore((state) => ({ ...state, checkout: freshCheckout }));
                            newCheckout = await addLineItems(freshCheckout.id, lineItemsToAdd);
                        }

                        updateStore((state) => ({ ...state, checkout: newCheckout, adding: false }));
                        navigate(newCheckout.checkoutUrl);
                        return newCheckout;
                    } catch (error) {
                        updateStore((state) => ({ ...state, adding: false }));
                        throw error;
                    }
                },
                removeLineItem: async (checkoutID, lineItemID) => {
                    const { removeLineItems } = await import('../utils/storefront-client');
                    const newCheckout = await removeLineItems(checkoutID, [lineItemID]);
                    updateStore((state) => ({ ...state, checkout: newCheckout }));
                },
                updateLineItem: async (checkoutID, lineItemID, quantity) => {
                    const lineItemsToUpdate = [{ id: lineItemID, quantity: parseInt(quantity, 10) }];
                    const { updateLineItems } = await import('../utils/storefront-client');
                    const newCheckout = await updateLineItems(checkoutID, lineItemsToUpdate);
                    updateStore((state) => ({ ...state, checkout: newCheckout }));
                },
                updateFilterType: (type) => {
                    updateStore((state) => ({ ...state, filteredType: type }));
                },
                updateFilterSort: (sort) => {
                    updateStore((state) => ({ ...state, filteredSort: sort }));
                },
                setValue: (value) => {
                    isBrowser && localStorage.setItem('customerAccessToken', JSON.stringify(value));
                    updateStore((state) => ({ ...state, customerAccessToken: value }));
                },
            }}
        >
            {children}
        </StoreContext.Provider>
    );
};

export default Provider;
